import {
    Banknote,
    BarChart3,
    CheckCircle,
    ChevronLeft,
    ChevronRight,
    Clock,
    Copy,
    Landmark,
    Plus,
    RefreshCw,
    Wallet,
    XCircle,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styles from '../../../assets/scss/pages/BillList.module.scss';
import api from '../../../config/axios-conf';
import { useAdminHeaderSlot } from '../../../contexts/AdminHeaderContext';
import { formatCurrency, formatPhoneNumber } from '../../../utils/formatters';
import Dropdown from '../../../components/ui/Dropdown';

const STATUS_MAP = {
    paid: { label: 'Thanh toán toàn bộ', class: 'paid' },
    pending: { label: 'Chưa thanh toán', class: 'pending' },
    partial: { label: 'Thanh toán một phần', class: 'partial' },
    cancelled: { label: 'Huỷ đơn', class: 'cancelled' },
};

const STATUS_STYLE_MAP = {
    paid: styles.billPageStatusPaid,
    pending: styles.billPageStatusPending,
    partial: styles.billPageStatusPartial,
    unpaid: styles.billPageStatusUnpaid,
    cancelled: styles.billPageStatusCancelled,
};

const PAYMENT_ICONS = {
    'Tiền mặt': <Banknote />,
    'Chuyển khoản': <Landmark />,
};

const AVATAR_COLORS = ['#0098F6', '#22C55E', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#F97316'];

function getAvatarColor(name) {
    if (!name) return AVATAR_COLORS[0];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getInitials(name) {
    if (!name) return '?';
    return name
        .split(' ')
        .map((w) => w[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();
}

function formatDate(dateStr) {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const mins = String(d.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year}\n${hours}:${mins}`;
}

const PAGE_SIZE = 20;

// Lấy ngày hôm nay dạng yyyy-mm-dd
function getTodayStr() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Format yyyy-mm-dd → dd/mm/yyyy
function formatDateDisplay(dateStr) {
    if (!dateStr) return '...';
    const parts = dateStr.split('-');
    if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
    return dateStr;
}

function periodToDateRange(period) {
    const now = new Date();
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    switch (period) {
        case 'today': return { startDate: fmt(now), endDate: fmt(now) };
        case 'yesterday': { const d = new Date(now); d.setDate(d.getDate() - 1); return { startDate: fmt(d), endDate: fmt(d) }; }
        case '7days': { const d = new Date(now); d.setDate(d.getDate() - 6); return { startDate: fmt(d), endDate: fmt(now) }; }
        case 'thismonth': return { startDate: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`, endDate: fmt(now) };
        case 'lastmonth': {
            const y = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
            const m = now.getMonth() === 0 ? 12 : now.getMonth();
            const last = new Date(y, m, 0);
            return { startDate: `${y}-${String(m).padStart(2, '0')}-01`, endDate: fmt(last) };
        }
        default: return null;
    }
}

export default function BillList() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { setSlot } = useAdminHeaderSlot();
    const today = getTodayStr();
    const [showKpi, setShowKpi] = useState(false);
    const [toast, setToast] = useState(null);

    const showToast = (type, text) => {
        setToast({ type, text });
        setTimeout(() => setToast(null), 2500);
    };

    const handleCopy = async (text, successMsg, errorMsg) => {
        if (!text) return;
        try {
            await navigator.clipboard.writeText(text);
            showToast('success', successMsg);
        } catch {
            showToast('error', errorMsg);
        }
    };

    // Search từ URL params (header search navigate đến ?search=...)
    const searchKeyword = searchParams.get('search') || '';
    const isSearching = searchKeyword.trim().length > 0;

    // Data
    const [bills, setBills] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 0 });

    // KPI
    const [kpi, setKpi] = useState({
        totalRevenue: 0,
        totalBills: 0,
        pendingPayment: 0,
        unpaidCount: 0,
    });

    // Filters - đọc từ URL params hoặc mặc định hôm nay
    const [filters, setFilters] = useState(() => {
        const urlPeriod = searchParams.get('period');
        const urlStart = searchParams.get('startDate');
        const urlEnd = searchParams.get('endDate');

        if (urlStart && urlEnd) {
            return { startDate: urlStart, endDate: urlEnd, status: '', paymentMethod: '' };
        }
        if (urlPeriod) {
            const range = periodToDateRange(urlPeriod);
            if (range) return { ...range, status: '', paymentMethod: '' };
        }
        return { startDate: today, endDate: today, status: '', paymentMethod: '' };
    });

    const isToday = filters.startDate === today && filters.endDate === today;

    // Fetch bills theo date-range (mặc định)
    const fetchBills = useCallback(
        async (page = 1) => {
            if (isSearching) return; // Nếu đang search thì không fetch date-range
            setLoading(true);
            try {
                const params = {
                    limit: PAGE_SIZE,
                    page,
                    fromDate: filters.startDate || today,
                    toDate: filters.endDate || today,
                };
                if (filters.status) params.status = filters.status;
                if (filters.paymentMethod) params.paymentMethod = filters.paymentMethod;

                const { data } = await api.get('/bills/date-range', { params });
                const billsData = data.bills || data.data || [];
                const paginationData = data.pagination || {};

                setBills(billsData);
                setPagination({
                    page: paginationData.page || page,
                    total: paginationData.total || billsData.length,
                    totalPages: paginationData.totalPages || 1,
                });

                // Đếm theo status: lấy toàn bộ bills trong khoảng ngày (không filter status)
                try {
                    const { data: allData } = await api.get('/bills/date-range', {
                        params: { fromDate: filters.startDate || today, toDate: filters.endDate || today, limit: 10000, page: 1 },
                    });
                    const allBills = allData.bills || allData.data || [];
                    setKpi((prev) => ({
                        ...prev,
                        totalBills: allBills.filter((b) => b.status === 'paid').length,
                        unpaidCount: allBills.filter((b) => b.status === 'pending' || b.status === 'partial').length,
                    }));
                } catch {
                    // ignore
                }
            } catch (err) {
                console.error('Error fetching bills:', err);
                setBills([]);
            } finally {
                setLoading(false);
            }
        },
        [filters, today, isSearching],
    );

    // Search bills theo keyword từ URL
    const fetchSearchBills = useCallback(
        async (page = 1) => {
            if (!searchKeyword.trim()) return;
            setLoading(true);
            try {
                const { data } = await api.get('/bills/search', {
                    params: { keyword: searchKeyword.trim(), limit: PAGE_SIZE, page },
                });
                const billsData = data.bills || data.data || [];
                const paginationData = data.pagination || {};

                setBills(billsData);
                setPagination({
                    page: paginationData.page || page,
                    total: paginationData.total || billsData.length,
                    totalPages: paginationData.totalPages || 1,
                });
            } catch (err) {
                console.error('Error searching bills:', err);
                setBills([]);
            } finally {
                setLoading(false);
            }
        },
        [searchKeyword],
    );

    const fetchKpi = useCallback(async () => {
        const todayNow = getTodayStr();
        const isTodayFilter = filters.startDate === todayNow && filters.endDate === todayNow;

        try {
            if (isTodayFilter || (!filters.startDate && !filters.endDate)) {
                // Hôm nay → dùng API statistics (chính xác nhất)
                const { data } = await api.get('/statistics/today');
                setKpi({
                    totalRevenue: data.totalRevenue || 0,
                    totalBills: data.totalBills || 0,
                    pendingPayment: data.checkedInNotCheckedOut || 0,
                    unpaidCount: 0, // sẽ được cập nhật từ fetchBills
                });
            } else {
                // Khoảng thời gian khác → dùng API end-day-report-custom (giống app)
                const { data: report } = await api.get(
                    `/statistics/end-day-report-custom?startDate=${filters.startDate}&endDate=${filters.endDate}`,
                );
                setKpi({
                    totalRevenue: report.revenue || 0,
                    totalBills: report.totalBills || 0,
                    pendingPayment: report.totalOwed ? 1 : 0,
                    unpaidCount: 0, // sẽ được cập nhật từ fetchBills
                });
            }
        } catch {
            // ignore
        }
    }, [filters]);

    // Khi search thay đổi → fetch search, khi xoá search → fetch date-range
    useEffect(() => {
        if (isSearching) {
            fetchSearchBills(1);
        } else {
            fetchBills(1);
        }
    }, [isSearching, fetchSearchBills, fetchBills]);

    useEffect(() => {
        if (!isSearching) fetchKpi();
    }, [fetchKpi, isSearching]);

    const handlePageChange = (page) => {
        if (isSearching) {
            fetchSearchBills(page);
        } else {
            fetchBills(page);
        }
    };

    const handleFilterChange = (key, value) => {
        // Xoá search param khi đổi filter
        if (isSearching) navigate('/admin/bills', { replace: true });
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    const handleResetFilters = () => {
        if (isSearching) navigate('/admin/bills', { replace: true });
        setFilters({ startDate: today, endDate: today, status: '', paymentMethod: '' });
    };

    // Pagination rendering
    const renderPagination = () => {
        const { page, totalPages, total } = pagination;
        if (totalPages <= 1) return null;

        const pages = [];
        const start = Math.max(1, page - 2);
        const end = Math.min(totalPages, page + 2);

        if (start > 1) {
            pages.push(1);
            if (start > 2) pages.push('...');
        }
        for (let i = start; i <= end; i++) pages.push(i);
        if (end < totalPages) {
            if (end < totalPages - 1) pages.push('...');
            pages.push(totalPages);
        }

        const from = (page - 1) * PAGE_SIZE + 1;
        const to = Math.min(page * PAGE_SIZE, total);

        return (
            <div className={styles.billPagePagination}>
                <span className={styles.billPagePaginationInfo}>
                    Hiển thị {from} - {to} của {total} hoá đơn
                </span>
                <div className={styles.billPagePaginationControls}>
                    <button
                        className={styles.billPagePageBtn}
                        disabled={page <= 1}
                        onClick={() => handlePageChange(page - 1)}
                    >
                        <ChevronLeft />
                    </button>
                    {pages.map((p, i) =>
                        p === '...' ? (
                            <span key={`dots-${i}`} className={styles.billPagePageDots}>
                                ...
                            </span>
                        ) : (
                            <button
                                key={p}
                                className={`${styles.billPagePageBtn}${p === page ? ` ${styles.billPagePageBtnActive}` : ''}`}
                                onClick={() => handlePageChange(p)}
                            >
                                {p}
                            </button>
                        ),
                    )}
                    <button
                        className={styles.billPagePageBtn}
                        disabled={page >= totalPages}
                        onClick={() => handlePageChange(page + 1)}
                    >
                        <ChevronRight />
                    </button>
                </div>
            </div>
        );
    };

    useEffect(() => {
        setSlot(
            <div className={styles.headerSlot}>
                {!isSearching && (
                    <>
                        <input
                            type="date"
                            className={styles.hDateInput}
                            value={filters.startDate}
                            onChange={(e) => handleFilterChange('startDate', e.target.value)}
                        />
                        <span className={styles.hDash}>~</span>
                        <input
                            type="date"
                            className={styles.hDateInput}
                            value={filters.endDate}
                            onChange={(e) => handleFilterChange('endDate', e.target.value)}
                        />
                        <div className={styles.hDropdownWrap}>
                            <Dropdown
                                value={filters.status}
                                placeholder="Trạng thái"
                                onChange={(val) => handleFilterChange('status', val)}
                                options={[
                                    { value: '', label: 'Tất cả trạng thái' },
                                    { value: 'paid', label: 'Thanh toán toàn bộ', dot: '#22C55E' },
                                    { value: 'pending', label: 'Chưa thanh toán', dot: '#F59E0B' },
                                    { value: 'partial', label: 'Thanh toán một phần', dot: '#0098F6' },
                                    { value: 'cancelled', label: 'Huỷ đơn', dot: '#EF4444' },
                                ]}
                            />
                        </div>
                        <div className={styles.hDropdownWrap}>
                            <Dropdown
                                value={filters.paymentMethod}
                                placeholder="Phương thức"
                                onChange={(val) => handleFilterChange('paymentMethod', val)}
                                options={[
                                    { value: '', label: 'Tất cả phương thức' },
                                    { value: 'Tiền mặt', label: 'Tiền mặt' },
                                    { value: 'Chuyển khoản', label: 'Chuyển khoản' },
                                ]}
                            />
                        </div>
                        <button className={styles.hBtnGhost} onClick={handleResetFilters} title="Làm mới bộ lọc">
                            <RefreshCw />
                        </button>
                        <div className={styles.hDivider} />
                        <button className={styles.hBtnGhost} onClick={() => setShowKpi((p) => !p)}>
                            <BarChart3 /> Số liệu
                        </button>
                    </>
                )}
                <button className={styles.hBtnPrimary} onClick={() => navigate('/admin/bills/create')}>
                    <Plus /> Tạo hoá đơn
                </button>
            </div>,
        );
        return () => setSlot(null);
    }, [setSlot, isSearching, filters, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className={styles.billPage}>
            {/* Hiển thị keyword đang search */}
            {isSearching && (
                <div style={{ marginBottom: 16, fontSize: 14, color: '#6B7280' }}>
                    Kết quả tìm kiếm cho: <strong style={{ color: '#1A1A1A' }}>"{searchKeyword}"</strong>
                    {' — '}
                    <button
                        onClick={() => navigate('/admin/bills', { replace: true })}
                        style={{
                            color: '#0098F6',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontFamily: 'inherit',
                            fontSize: 'inherit',
                        }}
                    >
                        Xoá tìm kiếm
                    </button>
                </div>
            )}

            {/* ===== KPI CARDS ===== */}
            {!isSearching && showKpi && (
                <div className={styles.billPageKpis}>
                    <div className={styles.billPageKpi}>
                        <div className={styles.billPageKpiContent}>
                            <span className={styles.billPageKpiLabel}>TỔNG DOANH THU</span>
                            <span className={styles.billPageKpiValue}>{formatCurrency(kpi.totalRevenue)}</span>
                            <span className={`${styles.billPageKpiChange} ${styles.billPageKpiChangeNeutral}`}>
                                {isToday
                                    ? 'Hôm nay'
                                    : `${formatDateDisplay(filters.startDate)} - ${formatDateDisplay(filters.endDate)}`}
                            </span>
                        </div>
                        <div className={`${styles.billPageKpiIcon} ${styles.billPageKpiIconBlue}`}>
                            <Wallet />
                        </div>
                    </div>

                    <div className={styles.billPageKpi}>
                        <div className={styles.billPageKpiContent}>
                            <span className={styles.billPageKpiLabel}>HOÁ ĐƠN ĐÃ TRẢ</span>
                            <span className={styles.billPageKpiValue}>
                                {kpi.totalBills}
                            </span>
                            <span className={`${styles.billPageKpiChange} ${styles.billPageKpiChangeNeutral}`}>
                                {isToday
                                    ? 'Hôm nay'
                                    : `${formatDateDisplay(filters.startDate)} - ${formatDateDisplay(filters.endDate)}`}
                            </span>
                        </div>
                        <div className={`${styles.billPageKpiIcon} ${styles.billPageKpiIconGreen}`}>
                            <CheckCircle />
                        </div>
                    </div>

                    {isToday && (
                        <div className={styles.billPageKpi}>
                            <div className={styles.billPageKpiContent}>
                                <span className={styles.billPageKpiLabel}>CHỜ PHỤC VỤ</span>
                                <span className={styles.billPageKpiValue}>{kpi.pendingPayment}</span>
                                <span className={`${styles.billPageKpiChange} ${styles.billPageKpiChangeNeutral}`}>
                                    Cần xử lý ngay
                                </span>
                            </div>
                            <div className={`${styles.billPageKpiIcon} ${styles.billPageKpiIconOrange}`}>
                                <Clock />
                            </div>
                        </div>
                    )}

                    <div className={styles.billPageKpi}>
                        <div className={styles.billPageKpiContent}>
                            <span className={styles.billPageKpiLabel}>CHƯA TT / TT 1 PHẦN</span>
                            <span className={styles.billPageKpiValue}>{kpi.unpaidCount}</span>
                            <span className={`${styles.billPageKpiChange} ${styles.billPageKpiChangeNeutral}`}>
                                {isToday
                                    ? 'Hôm nay'
                                    : `${formatDateDisplay(filters.startDate)} - ${formatDateDisplay(filters.endDate)}`}
                            </span>
                        </div>
                        <div className={`${styles.billPageKpiIcon} ${styles.billPageKpiIconRed}`}>
                            <XCircle />
                        </div>
                    </div>
                </div>
            )}

            {/* ===== TABLE ===== */}
            <div className={styles.billPageTableWrapper}>
                {loading ? (
                    <div className={styles.billPageLoading}>
                        <div className={styles.billPageSpinner} />
                        Đang tải hoá đơn...
                    </div>
                ) : bills.length === 0 ? (
                    <div className={styles.billPageEmpty}>Không có hoá đơn nào</div>
                ) : (
                    <>
                        <table className={styles.billPageTable}>
                            <thead>
                                <tr>
                                    <th>MÃ HĐ</th>
                                    <th>KHÁCH HÀNG</th>
                                    <th>TỔNG TIỀN</th>
                                    <th>GIẢM GIÁ</th>
                                    <th>THANH TOÁN</th>
                                    <th>TRẠNG THÁI</th>
                                    <th>NGÀY TẠO</th>
                                </tr>
                            </thead>
                            <tbody>
                                {bills.map((bill) => {
                                    const customerName = bill.customer?.name || 'Khách lẻ';
                                    const customerPhone = bill.customer?.phone || '';
                                    const statusInfo = STATUS_MAP[bill.status] || STATUS_MAP.paid;
                                    const paymentIcon = PAYMENT_ICONS[bill.paymentMethod] || <Banknote />;
                                    const statusClass = STATUS_STYLE_MAP[statusInfo.class] || '';

                                    return (
                                        <tr
                                            key={bill.id}
                                            className={styles.billPageRowClickable}
                                            onClick={() => navigate(`/admin/bills/${bill.id}`)}
                                        >
                                            <td onClick={(e) => e.stopPropagation()}>
                                                <span
                                                    className={`${styles.billPageBillId} ${styles.copyable}`}
                                                    onClick={() =>
                                                        handleCopy(
                                                            bill.code || `#${bill.id}`,
                                                            'Đã copy mã hoá đơn',
                                                            'Copy mã hoá đơn thất bại',
                                                        )
                                                    }
                                                    title="Bấm để copy mã hoá đơn"
                                                >
                                                    {bill.code || `#${bill.id}`}
                                                    <Copy size={12} className={styles.copyIcon} />
                                                </span>
                                            </td>
                                            <td>
                                                <div className={styles.billPageCustomer}>
                                                    <div
                                                        className={styles.billPageCustomerAvatar}
                                                        style={{ background: getAvatarColor(customerName) }}
                                                    >
                                                        {getInitials(customerName)}
                                                    </div>
                                                    <div className={styles.billPageCustomerInfo}>
                                                        <span className={styles.billPageCustomerName}>
                                                            {customerName}
                                                        </span>
                                                        {customerPhone && (
                                                            <span
                                                                className={`${styles.billPageCustomerPhone} ${styles.copyable}`}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleCopy(
                                                                        customerPhone,
                                                                        'Đã copy số điện thoại',
                                                                        'Copy số điện thoại thất bại',
                                                                    );
                                                                }}
                                                                title="Bấm để copy số điện thoại"
                                                            >
                                                                {formatPhoneNumber(customerPhone)}
                                                                <Copy size={11} className={styles.copyIcon} />
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <span className={styles.billPageAmount}>
                                                    {formatCurrency(bill.total)}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={styles.billPageDiscount}>
                                                    {bill.discount > 0 ? formatCurrency(bill.discount) : '0đ'}
                                                </span>
                                            </td>
                                            <td>
                                                <div className={styles.billPagePayment}>
                                                    {paymentIcon}
                                                    {bill.paymentMethod || 'Tiền mặt'}
                                                </div>
                                            </td>
                                            <td>
                                                <span className={`${styles.billPageStatus} ${statusClass}`}>
                                                    {statusInfo.label}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={styles.billPageDate}>
                                                    {formatDate(bill.checkoutTime)}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        {renderPagination()}
                    </>
                )}
            </div>

            {toast && (
                <div
                    className={`${styles.toast} ${toast.type === 'success' ? styles.toastSuccess : styles.toastError}`}
                >
                    {toast.type === 'success' ? <CheckCircle size={15} /> : null}
                    {toast.text}
                </div>
            )}
        </div>
    );
}
